import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calculator, ArrowLeftRight, Percent, Euro, Trash2, AlertTriangle, Microscope, ChevronDown, ChevronUp, Globe, Plus, Minus, Moon, Sun, Languages, List, Layers, Copy, Check, HelpCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import MoneyVisualization from './MoneyVisualization';
import CookieConsent from './CookieConsent';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  getPVNRates,
  getCountryRates,
  CALCULATION_MODES,
  DEFAULT_MODE,
  calculatePVN,
  formatCurrency,
  formatExact
} from '../data/mock';
import { getTranslation } from '../data/translations';

const PVNCalculator = () => {
  const [lang, setLang] = useState('lv');
  const [darkMode, setDarkMode] = useState(false);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [amounts, setAmounts] = useState(['']);
  const [rate, setRate] = useState(getCountryRates(DEFAULT_COUNTRY)[0]);
  
  const t = (key) => getTranslation(lang, key);
  const [customRate, setCustomRate] = useState('');
  const [isCustomRate, setIsCustomRate] = useState(false);
  const [mode, setMode] = useState(DEFAULT_MODE);
  const [results, setResults] = useState([]);
  const [totalResult, setTotalResult] = useState(null);
  const [showExact, setShowExact] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showTotal, setShowTotal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  const pvnRates = getPVNRates(country);
  const effectiveRate = isCustomRate ? (parseFloat(customRate) || 0) : rate;

  // Apply dark mode to HTML element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Update rate when country changes
  useEffect(() => {
    const countryRates = getCountryRates(country);
    setRate(countryRates[0]);
    setIsCustomRate(false);
    setCustomRate('');
  }, [country]);

  // Calculate results for all amounts
  useEffect(() => {
    const newResults = amounts.map(amt => {
      if (amt && parseFloat(amt) > 0) {
        return calculatePVN(amt, effectiveRate, mode);
      }
      return null;
    });
    setResults(newResults);

    // Calculate total
    const totalAmount = amounts.reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0);
    if (totalAmount > 0) {
      setTotalResult(calculatePVN(totalAmount, effectiveRate, mode));
    } else {
      setTotalResult(null);
    }
  }, [amounts, effectiveRate, mode]);

  const handleReset = () => {
    setAmounts(['']);
    setCountry(DEFAULT_COUNTRY);
    setRate(getCountryRates(DEFAULT_COUNTRY)[0]);
    setCustomRate('');
    setIsCustomRate(false);
    setMode(DEFAULT_MODE);
    setResults([]);
    setTotalResult(null);
    setShowExact(false);
    setShowTotal(false);
  };

  const handleAmountChange = (index, value) => {
    value = value.replace(/,/g, '.');
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const newAmounts = [...amounts];
      newAmounts[index] = value;
      setAmounts(newAmounts);
    }
  };

  const addAmountField = () => {
    if (amounts.length < 10) {
      setAmounts([...amounts, '']);
    }
  };

  const removeAmountField = (index) => {
    if (amounts.length > 1) {
      const newAmounts = amounts.filter((_, i) => i !== index);
      setAmounts(newAmounts);
    }
  };

  const handleCustomRateChange = (e) => {
    let value = e.target.value;
    value = value.replace(/,/g, '.');
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCustomRate(value);
    }
  };

  const handleRateSelect = (rateValue) => {
    if (rateValue === 'custom') {
      setIsCustomRate(true);
    } else {
      setIsCustomRate(false);
      setRate(rateValue);
    }
  };

  // Calculate display amount for visualization
  const displayAmount = totalResult ? (mode === 'add' ? totalResult.amountWithPVN : totalResult.amountWithoutPVN) : 0;

  // Copy result to clipboard
  const copyToClipboard = async () => {
    const resultValue = totalResult ? (mode === 'add' ? totalResult.amountWithPVN : totalResult.amountWithoutPVN) : 0;
    // Format with comma as decimal separator (European format)
    const formattedValue = resultValue.toFixed(2).replace('.', ',');
    try {
      await navigator.clipboard.writeText(formattedValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers or non-HTTPS
      const textArea = document.createElement('textarea');
      textArea.value = formattedValue;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Check if there are multiple valid results
  const validResults = results.filter(r => r !== null);
  const hasMultipleRows = amounts.length > 1;

  return (
    <div className={`min-h-screen px-4 transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
        : 'bg-gradient-to-br from-slate-50 via-white to-slate-100'
    }`} style={{ paddingTop: '1.5cm' }}>
      <div className="max-w-2xl mx-auto">
        {/* Main Calculator Card */}
        <Card className={`border-0 shadow-xl overflow-hidden ${
          darkMode ? 'shadow-black/30 bg-slate-800' : 'shadow-slate-200/50 bg-white'
        }`}>
          <CardHeader className={`py-3 ${darkMode ? 'bg-slate-900' : 'bg-slate-800'} text-white`}>
            <div className="flex items-center justify-between">
              {/* Title on the left */}
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <span className="bg-emerald-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                  {lang === 'lv' ? 'PVN' : 'VAT'}
                </span>
                {lang === 'lv' ? 'kalkulators' : 'calculator'}
              </h1>
              
              {/* Theme, Language and FAQ Toggle on the right */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLang(lang === 'lv' ? 'en' : 'lv')}
                  className="px-2 py-1 rounded font-medium text-sm transition-all text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  {lang === 'lv' ? 'LV' : 'EN'}
                </button>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-1.5 rounded transition-all text-slate-300 hover:text-yellow-400 hover:bg-slate-700"
                >
                  {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setShowFAQ(!showFAQ)}
                  className={`p-1.5 rounded transition-all ${showFAQ ? 'text-emerald-400 bg-slate-700' : 'text-slate-300 hover:text-emerald-400 hover:bg-slate-700'}`}
                  title="FAQ"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className={`p-4 space-y-4 ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
            {/* Country Selection */}
            <div className="space-y-2">
              <Label className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <Globe className="w-4 h-4 text-emerald-500" />
                {t('country')}
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {COUNTRIES.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCountry(c.code)}
                    className={`h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                      country === c.code
                        ? `border-emerald-500 shadow-md ${darkMode ? 'bg-emerald-900/50' : 'bg-emerald-50'}`
                        : `${darkMode ? 'border-slate-600 bg-slate-700 hover:border-emerald-400 hover:bg-slate-600' : 'border-slate-200 hover:border-emerald-300 bg-white hover:bg-emerald-50'}`
                    }`}
                  >
                    {c.code === 'LV' && (
                      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded shadow-sm">
                        <rect width="24" height="16" fill="#9E3039" rx="2"/>
                        <rect y="6" width="24" height="4" fill="#FFFFFF"/>
                      </svg>
                    )}
                    {c.code === 'LT' && (
                      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded shadow-sm">
                        <rect width="24" height="5.33" fill="#FDB913" rx="2"/>
                        <rect y="5.33" width="24" height="5.33" fill="#006A44"/>
                        <rect y="10.67" width="24" height="5.33" fill="#C1272D"/>
                      </svg>
                    )}
                    {c.code === 'EE' && (
                      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded shadow-sm">
                        <rect width="24" height="16" fill="#FFFFFF" rx="2"/>
                        <rect width="24" height="10.67" fill="#000000"/>
                        <rect width="24" height="5.33" fill="#0072CE"/>
                      </svg>
                    )}
                    <span className={`text-sm font-medium ${
                      country === c.code 
                        ? (darkMode ? 'text-emerald-400' : 'text-emerald-700') 
                        : (darkMode ? 'text-slate-300' : 'text-slate-600')
                    }`}>
                      {t(c.code === 'LV' ? 'latvia' : c.code === 'LT' ? 'lithuania' : 'estonia')}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* PVN Rate Selection */}
            <div className="space-y-2">
              <Label className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <Percent className="w-4 h-4 text-emerald-500" />
                {t('pvnRate')}
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {pvnRates.map((rateOption) => (
                  rateOption.value === 'custom' ? (
                    <div
                      key={rateOption.value}
                      onClick={() => handleRateSelect(rateOption.value)}
                      className={`h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center cursor-pointer ${
                        isCustomRate
                          ? 'border-emerald-500 bg-emerald-500 shadow-lg shadow-emerald-500/30'
                          : `${darkMode ? 'border-slate-600 bg-slate-700 hover:border-emerald-400' : 'border-slate-200 hover:border-emerald-300 bg-white hover:bg-emerald-50'}`
                      }`}
                    >
                      {isCustomRate ? (
                        <div className="flex items-center justify-center w-full px-2">
                          <input
                            type="text"
                            value={customRate}
                            onChange={handleCustomRateChange}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="0"
                            autoFocus
                            className="w-full bg-transparent text-white text-base font-bold text-center outline-none placeholder-emerald-200"
                          />
                          <span className="text-white font-bold text-base">%</span>
                        </div>
                      ) : (
                        <span className={`text-base font-bold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{t('other')}</span>
                      )}
                    </div>
                  ) : (
                    <button
                      key={rateOption.value}
                      onClick={() => handleRateSelect(rateOption.value)}
                      className={`h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
                        !isCustomRate && rate === rateOption.value
                          ? 'border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                          : `${darkMode ? 'border-slate-600 bg-slate-700 text-slate-300 hover:border-slate-500' : 'border-slate-200 hover:border-emerald-300 bg-white text-slate-700 hover:bg-emerald-50'}`
                      }`}
                    >
                      <div className="text-base font-bold">{rateOption.label}</div>
                    </button>
                  )
                ))}
              </div>
            </div>

            {/* Calculation Mode */}
            <div className="space-y-2">
              <Label className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <ArrowLeftRight className="w-4 h-4 text-emerald-500" />
                {t('calcType')}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {CALCULATION_MODES.map((modeOption) => (
                  <button
                    key={modeOption.value}
                    onClick={() => setMode(modeOption.value)}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      mode === modeOption.value
                        ? `border-emerald-500 ${darkMode ? 'bg-emerald-900/50' : 'bg-emerald-50'}`
                        : `${darkMode ? 'border-slate-600 bg-slate-700 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300 bg-white'}`
                    }`}
                  >
                    <span className={`text-sm font-medium ${
                      mode === modeOption.value 
                        ? (darkMode ? 'text-emerald-400' : 'text-emerald-700')
                        : (darkMode ? 'text-slate-300' : 'text-slate-700')
                    }`}>
                      {modeOption.value === 'add' ? t('sumWithoutPVN') : t('sumWithPVN')}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Input(s) */}
            <div className="space-y-2">
              <Label className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <Euro className="w-4 h-4 text-emerald-500" />
                {mode === 'add' ? t('sumWithoutPVN') : t('sumWithPVN')}
              </Label>
              {amounts.map((amount, index) => (
                <div key={index} className="flex items-center gap-2">
                  {/* Row number - show only when multiple rows */}
                  {hasMultipleRows && (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 border-slate-800 ${
                      index === 0 ? 'bg-yellow-400 text-slate-800' :
                      index === 1 ? 'bg-gray-300 text-slate-800' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-white text-slate-800'
                    }`}>
                      {index + 1}.
                    </div>
                  )}
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      value={amount}
                      onChange={(e) => handleAmountChange(index, e.target.value)}
                      placeholder="0.00"
                      className={`text-base h-10 pl-3 pr-12 transition-all ${
                        darkMode 
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-emerald-500' 
                          : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-500'
                      }`}
                    />
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 font-medium text-sm ${
                      darkMode ? 'text-slate-400' : 'text-slate-400'
                    }`}>
                      EUR
                    </span>
                  </div>
                  {/* Delete button: show on all rows except first (index > 0), delete from bottom up */}
                  {index > 0 ? (
                    <button
                      onClick={() => removeAmountField(index)}
                      className={`w-10 h-10 rounded-lg border-2 transition-all flex items-center justify-center ${
                        darkMode
                          ? 'border-red-700 bg-red-900/50 text-red-400 hover:bg-red-800/50 hover:border-red-600'
                          : 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:border-red-300'
                      }`}
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                  ) : amounts.length < 10 ? (
                    <button
                      onClick={addAmountField}
                      className={`w-10 h-10 rounded-lg border-2 transition-all flex items-center justify-center ${
                        darkMode
                          ? 'border-emerald-700 bg-emerald-900/50 text-emerald-400 hover:bg-emerald-800/50 hover:border-emerald-600'
                          : 'border-emerald-300 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:border-emerald-400'
                      }`}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  ) : (
                    <div className="w-10 h-10" /> 
                  )}
                </div>
              ))}
            </div>

            {/* Show Individual/Total Toggle - only when multiple rows */}
            {hasMultipleRows && (
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-between flex-1 p-2 rounded-lg ${
                  darkMode ? 'bg-slate-700' : 'bg-slate-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {showTotal ? <Layers className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} /> : <List className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />}
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {showTotal ? t('showTotal') : t('showEach')}
                    </span>
                  </div>
                  <Switch
                    checked={showTotal}
                    onCheckedChange={setShowTotal}
                    className={darkMode ? 'data-[state=checked]:bg-emerald-700 data-[state=unchecked]:bg-slate-600' : ''}
                  />
                </div>
                {/* Spacer to align with trash button below */}
                <div className="w-10 h-10" />
              </div>
            )}

            {/* Toggle and Reset in one row */}
            <div className="flex items-center justify-between gap-2">
              <div className={`flex items-center justify-between flex-1 p-2 rounded-lg ${
                darkMode ? 'bg-slate-700' : 'bg-slate-50'
              }`}>
                <div className="flex items-center gap-2">
                  <Microscope className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                  <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{t('showExact')}</span>
                </div>
                <Switch
                  checked={showExact}
                  onCheckedChange={setShowExact}
                  className={darkMode ? 'data-[state=checked]:bg-emerald-700 data-[state=unchecked]:bg-slate-600' : ''}
                />
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={handleReset}
                className={`h-10 w-10 transition-all ${
                  darkMode
                    ? 'border-red-700 bg-red-900/50 text-red-400 hover:bg-red-800/50 hover:text-red-300 hover:border-red-600'
                    : 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 hover:border-red-300'
                }`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className={`mt-3 border-0 shadow-xl transition-all duration-500 overflow-hidden ${
          darkMode ? 'shadow-black/30 bg-slate-800' : 'shadow-slate-200/50 bg-white'
        } ${totalResult ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-2'}`}>
          <CardHeader className={`py-3 ${darkMode ? 'bg-slate-900' : 'bg-slate-800'} text-white`}>
            <CardTitle className="text-lg flex items-center justify-center gap-2 relative">
              <Calculator className="w-4 h-4 text-emerald-400" />
              {t('result')}
              {showExact && totalResult?.hasRoundingIssue && (
                <span className="absolute right-0 flex items-center gap-1 text-amber-400 text-xs font-normal">
                  <AlertTriangle className="w-3 h-3" />
                  {t('rounding')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className={`p-0 ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
              
              {/* Show Individual Results */}
              {!showTotal && hasMultipleRows ? (
                <>
                  {results.map((result, index) => result && (
                    <div key={index} className={`px-3 py-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 border-slate-800 ${
                            index === 0 ? 'bg-yellow-400 text-slate-800' :
                            index === 1 ? 'bg-gray-300 text-slate-800' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            'bg-white text-slate-800'
                          }`}>
                            {index + 1}.
                          </div>
                          <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {formatCurrency(mode === 'add' ? result.amountWithoutPVN : result.amountWithPVN)}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            +{formatCurrency(result.pvnAmount)} {t('vat')}
                          </div>
                          <div className={`text-lg font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            = {formatCurrency(mode === 'add' ? result.amountWithPVN : result.amountWithoutPVN)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Total row */}
                  <div className={`px-3 py-2 ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-md ${darkMode ? 'bg-emerald-600 shadow-emerald-600/30' : 'bg-emerald-500 shadow-emerald-500/30'}`}>
                          <Calculator className="w-4 h-4 text-white" />
                        </div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                          {t('total')}
                        </span>
                      </div>
                      <div className={`text-2xl font-extrabold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        {totalResult ? formatCurrency(mode === 'add' ? totalResult.amountWithPVN : totalResult.amountWithoutPVN) : '€0,00'}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Input Value */}
                  <div className={`flex items-center justify-between px-3 py-2 transition-colors ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                        <Euro className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {mode === 'add' ? t('sumWithoutPVN') : t('sumWithPVN')}
                        {amounts.length > 1 && ` (${amounts.length})`}
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                      {totalResult ? formatCurrency(mode === 'add' ? totalResult.amountWithoutPVN : totalResult.amountWithPVN) : '€0,00'}
                    </div>
                  </div>

                  {/* PVN Amount */}
                  <div className={`flex items-center justify-between px-3 py-2 transition-colors ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                        <Percent className={`w-4 h-4 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{t('vat')} ({effectiveRate}%)</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                        {totalResult ? formatCurrency(totalResult.pvnAmount) : '€0,00'}
                      </div>
                      {showExact && totalResult && totalResult.pvnAmountExact !== totalResult.pvnAmount && (
                        <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('exact')}: {formatExact(totalResult.pvnAmountExact)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Main Result */}
                  <div className={`flex items-center justify-between px-3 py-2 ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-md ${darkMode ? 'bg-emerald-600 shadow-emerald-600/30' : 'bg-emerald-500 shadow-emerald-500/30'}`}>
                        <Calculator className="w-4 h-4 text-white" />
                      </div>
                      <div className={`text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        {mode === 'add' ? t('sumWithPVN') : t('sumWithoutPVN')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        {totalResult ? formatCurrency(mode === 'add' ? totalResult.amountWithPVN : totalResult.amountWithoutPVN) : '€0,00'}
                      </div>
                      {showExact && totalResult && mode === 'extract' && totalResult.amountWithoutPVNExact !== totalResult.amountWithoutPVN && (
                        <div className={`text-xs ${darkMode ? 'text-emerald-500' : 'text-emerald-500'}`}>
                          {t('exact')}: {formatExact(totalResult.amountWithoutPVNExact)}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Money Visualization */}
              {displayAmount > 0 && (
                <div className={`px-3 py-2 border-t ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-100'}`}>
                  <MoneyVisualization amount={displayAmount} />
                </div>
              )}

              {/* Copy Result Button */}
              {totalResult && (
                <div className={`px-3 py-2 border-t ${darkMode ? 'border-slate-600' : 'border-slate-100'}`}>
                  <button
                    onClick={copyToClipboard}
                    className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      copied
                        ? (darkMode ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white')
                        : (darkMode ? 'bg-slate-600 text-slate-200 hover:bg-slate-500' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('copied') : t('copyResult')}
                  </button>
                </div>
              )}
            </div>

            {/* Rounding Warning */}
            {showExact && totalResult?.hasRoundingIssue && (
              <div className={`p-4 border-t ${darkMode ? 'bg-amber-900/20 border-amber-900/30' : 'bg-amber-50 border-amber-100'}`}>
                <div 
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => setShowExplanation(!showExplanation)}
                >
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className={`text-sm font-medium ${darkMode ? 'text-amber-400' : 'text-amber-800'}`}>
                        {t('roundingWarning')}
                      </div>
                      {showExplanation ? (
                        <ChevronUp className={`w-4 h-4 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                      ) : (
                        <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                      )}
                    </div>
                    <div className={`text-xs mt-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      {t('roundingExplain1')} ({formatCurrency(totalResult.recalculated?.amountWithoutPVN)}), 
                      {t('roundingExplain2')} {formatCurrency(totalResult.recalculated?.amountWithPVN)}, {t('roundingExplain3')} {formatCurrency(totalResult.amountWithPVN)}.
                    </div>
                    
                    {showExplanation && (
                      <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-amber-900/30' : 'bg-white border-amber-200'}`}>
                        <div className={`text-xs space-y-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{t('whyHappens')}</p>
                          <p>
                            {t('whyExplain1')} {formatCurrency(totalResult.amountWithPVN)}, 
                            {t('whyExplain2')} {formatExact(totalResult.amountWithoutPVNExact)}.
                          </p>
                          <p>
                            {t('whyExplain3')} ({formatCurrency(totalResult.amountWithoutPVN)}) 
                            {t('whyExplain4')}
                          </p>
                          <div className={`p-2 rounded font-mono text-xs ${darkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                            {totalResult.recalculated?.amountWithoutPVN} × {rate}% = {formatCurrency(totalResult.recalculated?.pvnAmount)}<br/>
                            {totalResult.recalculated?.amountWithoutPVN} + {totalResult.recalculated?.pvnAmount} = {formatCurrency(totalResult.recalculated?.amountWithPVN)}
                          </div>
                          <p className={darkMode ? 'text-amber-400' : 'text-amber-700'}>
                            {t('whyExplain5')} {formatCurrency(totalResult.amountWithoutPVN)} 
                            {t('whyExplain6')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* FAQ Modal */}
      {showFAQ && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 bg-black/50 overflow-y-auto" onClick={() => setShowFAQ(false)}>
          <div 
            className={`relative w-full max-w-2xl rounded-xl shadow-2xl ${
              darkMode ? 'bg-slate-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* FAQ Header */}
            <div className={`flex items-center justify-between p-4 border-b ${
              darkMode ? 'border-slate-700' : 'border-slate-200'
            }`}>
              <h2 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                <HelpCircle className="w-5 h-5 text-emerald-500" />
                {lang === 'lv' ? 'BUJ' : 'FAQ'}
              </h2>
              <button
                onClick={() => setShowFAQ(false)}
                className={`p-1.5 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* FAQ Content */}
            <div className={`p-4 space-y-5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              <div>
                <h3 className={`text-base font-semibold mb-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('seoTitle1')}
                </h3>
                <p className="text-sm leading-relaxed">{t('seoText1')}</p>
              </div>

              <div>
                <h3 className={`text-base font-semibold mb-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('seoTitle2')}
                </h3>
                <p className="text-sm leading-relaxed">{t('seoText2')}</p>
              </div>

              <div>
                <h3 className={`text-base font-semibold mb-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('seoTitle3')}
                </h3>
                <p className="text-sm leading-relaxed">{t('seoText3')}</p>
              </div>

              <div>
                <h3 className={`text-base font-semibold mb-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('seoTitle4')}
                </h3>
                <p className="text-sm leading-relaxed">{t('seoText4')}</p>
              </div>

              <div>
                <h3 className={`text-base font-semibold mb-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('seoTitle5')}
                </h3>
                <p className="text-sm leading-relaxed whitespace-pre-line">{t('seoText5')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={`mt-8 pb-16 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <div className="space-y-1">
          <p><span className="font-medium">SIA AP International</span> · Visas tiesības aizsargātas.</p>
          <p>
            <a 
              href="mailto:info@apinternational.lv" 
              className={`hover:underline ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-600'}`}
            >
              info@apinternational.lv
            </a>
            {' · '}
            <a 
              href="https://apinternational.lv/" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`hover:underline ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-600'}`}
            >
              Mārketinga pakalpojumi
            </a>
          </p>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent darkMode={darkMode} lang={lang} />
    </div>
  );
};

export default PVNCalculator;
